import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SEO from "../../components/SEO/SEO";
import { useDispatch, useSelector } from "react-redux";
import {
  FaEnvelope,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
} from "react-icons/fa";
import {
  verifyEmail as verifyEmailAction,
  resendVerification as resendVerificationAction,
  getUserInfo,
} from "../../store/slices/auth/asyncThunks";
import { selectResendPending } from "../../store/slices/auth/slice";
import {
  Container,
  VerificationCard,
  IconWrapper,
  Title,
  Message,
  ActionButton,
  ResendSection,
  ResendText,
  ResendButton,
} from "./EmailVerificationPage.styles";

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [verificationStatus, setVerificationStatus] = useState("pending"); // pending, success, error
  const [resendCooldown, setResendCooldown] = useState(0);

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  // Redux selectors
  const isResendPending = useSelector(selectResendPending);

  useEffect(() => {
    if (token) {
      verifyEmail(token);
    }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    let interval;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const verifyEmail = async (verificationToken) => {
    try {
      setVerificationStatus("pending");

      // Use Redux dispatch
      await dispatch(verifyEmailAction({ token: verificationToken })).unwrap();

      // isLoggedIn iz closure-a je zastareo (sesija se možda još obnavljala
      // kad se stranica učitala), pa uvek povlačimo sveže stanje sa servera —
      // time se i isEmailVerified u store-u sinhronizuje bez ručnog refresha
      let destination = "/pristup";
      try {
        const freshUser = await dispatch(getUserInfo()).unwrap();
        destination =
          freshUser?.user?.role === "admin" || freshUser?.role === "admin"
            ? "/admin-panel"
            : "/korisnicki-panel/kursevi";
      } catch {
        // nema aktivne sesije — korisnik ide na prijavu
      }

      setVerificationStatus("success");
      setTimeout(() => {
        navigate(destination);
      }, 3000);
    } catch (error) {
      console.error("Email verification error:", error);
      setVerificationStatus("error");
    }
  };

  const handleResendEmail = async () => {
    if (!email || isResendPending || resendCooldown > 0) return;

    try {
      // Use Redux dispatch
      await dispatch(resendVerificationAction({ email })).unwrap();

      setResendCooldown(60); // 60 seconds cooldown
    } catch (error) {
      console.error("Resend email error:", error);
    }
  };

  const renderContent = () => {
    if (!token) {
      return (
        <>
          <IconWrapper status="info">
            <FaEnvelope />
          </IconWrapper>
          <Title>Potvrdite svoj email</Title>
          <Message>
            Poslali smo link za potvrdu na vašu email adresu. Kliknite na link u
            email-u da potvrdite svoj nalog.
          </Message>
          {email && (
            <ResendSection>
              <ResendText>Niste dobili email?</ResendText>
              <ResendButton
                onClick={handleResendEmail}
                disabled={isResendPending || resendCooldown > 0}
              >
                {isResendPending ? (
                  <>
                    <l-line-spinner
                      size="24"
                      stroke="2"
                      speed="1"
                      color="var(--accent)"
                    />
                    Šalje se...
                  </>
                ) : resendCooldown > 0 ? (
                  `Ponovi za ${resendCooldown}s`
                ) : (
                  "Pošaljite ponovo"
                )}
              </ResendButton>
            </ResendSection>
          )}
          <ActionButton onClick={() => navigate("/pristup")}>
            Nazad na prijavljivanje
          </ActionButton>
        </>
      );
    }

    switch (verificationStatus) {
      case "pending":
        return (
          <>
            <IconWrapper status="pending">
              <FaSpinner className="spin" />
            </IconWrapper>
            <Title>Potvrđujemo vaš email...</Title>
            <Message>
              Molimo sačekajte dok potvrđujemo vašu email adresu.
            </Message>
          </>
        );

      case "success":
        return (
          <>
            <IconWrapper status="success">
              <FaCheckCircle />
            </IconWrapper>
            <Title>Email uspešno potvrđen!</Title>
            <Message>
              Vaš nalog je aktiviran. Automatski ćete biti preusmeren na
              stranicu za prijavljivanje.
            </Message>
            <ActionButton onClick={() => navigate("/pristup")}>
              Idite na prijavljivanje
            </ActionButton>
          </>
        );

      case "error":
        return (
          <>
            <IconWrapper status="error">
              <FaTimesCircle />
            </IconWrapper>
            <Title>Greška pri potvrdi</Title>
            <Message>
              Link za potvrdu je nevažeći ili je istekao. Molimo registrujte se
              ponovo ili zatražite novi link.
            </Message>
            <ActionButton onClick={() => navigate("/pristup")}>
              Registrujte se ponovo
            </ActionButton>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Container>
      <SEO title="Verifikacija naloga" path="/verifikacija" noindex />

      <VerificationCard>{renderContent()}</VerificationCard>
    </Container>
  );
};

export default EmailVerificationPage;
